import * as PIXI from 'pixi.js';
import {portalNormal, portalTangent} from './portalMath.js';
import {
	comparePortalDisplayOrder,
	getPortalBodyDisplayRoots,
} from './portalDisplayOrder.js';
import {
	mirrorPortalMaskQuads,
	sourcePortalMaskQuads,
} from './portalVisualMask.js';
import {PortalIntersectionParticles} from './PortalIntersectionParticles.js';

const copyTransform = (source, target) => {
	target.position.copyFrom(source.position);
	target.scale.copyFrom(source.scale);
	target.pivot.copyFrom(source.pivot);
	target.skew.copyFrom(source.skew);
	target.rotation = source.rotation;
	target.alpha = source.alpha;
	target.visible = source.visible;
	target.renderable = source.renderable;
	target.zIndex = source.zIndex;
	target.blendMode = source.blendMode;
	if('tint' in source && 'tint' in target) target.tint = source.tint;
};

const cloneMeshSimple = source => {
	const geometry = source.geometry;
	const positions = geometry.getBuffer('aPosition').data;
	const uvs = geometry.getBuffer('aUV').data;
	const indices = geometry.getIndex().data;
	const clone = new PIXI.MeshSimple({
		texture: source.texture,
		vertices: Float32Array.from(positions),
		uvs: Float32Array.from(uvs),
		indices: indices.slice(),
		topology: geometry.topology,
	});
	if(source.autoUpdate === false) {
		clone.autoUpdate = false;
		clone.onRender = null;
	}
	return clone;
};

export const clonePortalDisplayTree = source => {
	let clone;

	if(source instanceof PIXI.Graphics) {
		// Portal projectiles and other short-lived displays can be destroyed while
		// their mirror is still visible. A shallow clone would retain their owned
		// GraphicsContext; destroying the source then nulls its instructions while
		// Pixi is still collecting the mirror on the next frame.
		clone = source.clone(true);
	} else if(source instanceof PIXI.MeshSimple) {
		// Body tileTexture fills are custom TiledMesh instances (a MeshSimple
		// subclass). Treating an unknown mesh as a Container drops its geometry,
		// leaving only the transparent base graphic and outline at the exit.
		clone = cloneMeshSimple(source);
	} else if(source instanceof PIXI.TilingSprite) {
		clone = new PIXI.TilingSprite({
			texture: source.texture,
			width: source.width,
			height: source.height,
		});
		clone.anchor.copyFrom(source.anchor);
		clone.tilePosition.copyFrom(source.tilePosition);
		clone.tileScale.copyFrom(source.tileScale);
		clone.tileRotation = source.tileRotation;
	} else if(source instanceof PIXI.Sprite) {
		clone = new PIXI.Sprite({texture: source.texture});
		clone.anchor.copyFrom(source.anchor);
	} else if(source instanceof PIXI.Text) {
		clone = new PIXI.Text({text: source.text, style: source.style});
	} else {
		clone = new PIXI.Container();
	}

	copyTransform(source, clone);
	if(source.children && clone.addChild) {
		for(const child of source.children) {
			// Masks are rebuilt as portal-plane masks. Copying a source mask as a
			// normal child would make it visible in the duplicate.
			if(child === source.mask || child.isPortalPlaneMask) continue;
			clone.addChild(clonePortalDisplayTree(child));
		}
	}
	return clone;
};

const portalMaskFrame = (endpoint, ptm) => {
	const position = endpoint.body.GetPosition();
	return {
		center: {x: position.get_x() * ptm, y: position.get_y() * ptm},
		normal: portalNormal(endpoint.body.GetAngle()),
		tangent: portalTangent(endpoint.body.GetAngle()),
	};
};

const drawPortalQuad = (mask, frame, normalMin, normalMax, tangentMin, tangentMax, toLocal = null) => {
	const point = (normalDistance, tangentDistance) => {
		const world = {
			x: frame.center.x + frame.normal.x * normalDistance + frame.tangent.x * tangentDistance,
			y: frame.center.y + frame.normal.y * normalDistance + frame.tangent.y * tangentDistance,
		};
		return toLocal ? toLocal.applyInverse(world, world) : world;
	};
	const points = [
		point(normalMin, tangentMin),
		point(normalMin, tangentMax),
		point(normalMax, tangentMax),
		point(normalMax, tangentMin),
	];
	mask.poly(points.flatMap(({x, y}) => [x, y]), true);
	mask.fill({color: 0xffffff});
};

const drawPortalMask = (mask, endpoint, ptm, quads, toLocal = null) => {
	const frame = portalMaskFrame(endpoint, ptm);
	mask.clear();
	for(const quad of quads) {
		drawPortalQuad(
			mask,
			frame,
			quad.normalMin,
			quad.normalMax,
			quad.tangentMin,
			quad.tangentMax,
			toLocal,
		);
	}
};

// Transform of a display relative to the level container, rebuilt from local
// transforms so it is current right after the physics sync, before any render.
const displayToParentMatrix = (display, renderParent, matrix) => {
	matrix.identity();
	for(let node = display; node && node !== renderParent; node = node.parent) {
		node.updateLocalTransform();
		matrix.prepend(node.localTransform);
	}
	return matrix;
};

const tempMatrix = new PIXI.Matrix();
export const PORTAL_PLANE_MASK_LABEL = 'portal-plane-mask';

const canRevealBeyondOpening = (body, candidate) => {
	let hasSolidFixture = false;
	for(let fixture = body.GetFixtureList();
		Box2D.getPointer(fixture) !== Box2D.getPointer(Box2D.NULL);
		fixture = fixture.GetNext()) {
		if(fixture.IsSensor()) continue;
		hasSolidFixture = true;
		if(candidate.admittedFixtureKeys.has(Box2D.getPointer(fixture))) return true;
	}
	// Eyes and other sensor-only attachments follow the island's plane cut;
	// they have no solid fixture that independently gates their passage.
	return !hasSolidFixture;
};

export class PortalTransitVisual {
	constructor(candidate, renderParent, ptm) {
		this.candidate = candidate;
		this.renderParent = renderParent;
		this.ptm = ptm;
		this.container = null;
		this.entries = [];
		this.sourceMasks = [];
		this.intersectionParticles = [];
		try {
			this.build();
		} catch(error) {
			// Building attaches stencil masks to live level displays as it goes. A
			// failure part-way through (an unusual custom display object, a
			// destroyed child) must not leave those masks behind: a mask that is
			// never drawn clips its display to nothing for the rest of the run, and
			// the next transit would keep it as if it were a specialized mask.
			this.destroy();
			throw error;
		}
	}

	build() {
		const {candidate, renderParent, ptm} = this;
		this.container = new PIXI.Container();
		this.container.label = 'portal-mirror-visuals';
		renderParent.addChild(this.container);

		// Joint islands are discovered in physics traversal order, which has no
		// relationship to their display-list order. Flatten the visible roots and
		// sort them by their source paths before cloning so a character's limbs,
		// face, hat, and vehicle keep exactly the same layering at the exit portal.
		const displays = [];
		let discoveryOrder = 0;
		for(const body of candidate.bodies) {
			const mirrorBody = candidate.bodyMap.get(Box2D.getPointer(body));
			if(!mirrorBody) continue;

			for(const {sourceDisplay, orderPath} of getPortalBodyDisplayRoots(body, renderParent)) {
				displays.push({body, mirrorBody, sourceDisplay, orderPath, discoveryOrder: discoveryOrder++});
			}
		}

		displays.sort(comparePortalDisplayOrder);
		for(const display of displays) this.addDisplay(display);
		// Register each effect as soon as it exists so a failure in the second
		// constructor cannot orphan the first one's container in the level.
		this.intersectionParticles.push(new PortalIntersectionParticles({
			Box2D,
			parent: renderParent,
			ptm,
			getBodies: () => this.candidate.bodies,
			getEndpoint: () => this.candidate.source,
			depthDisplays: displays.map(display => display.sourceDisplay),
			depthParent: renderParent,
			label: 'portal-source-intersection-particles',
		}));
		this.intersectionParticles.push(new PortalIntersectionParticles({
			Box2D,
			parent: this.container,
			ptm,
			getBodies: () => [...this.candidate.bodyMap.values()],
			getEndpoint: () => this.candidate.target,
			label: 'portal-target-intersection-particles',
		}));
		this.update();
	}

	addDisplay({body, mirrorBody, sourceDisplay}) {
		const mirrorRoot = new PIXI.Container();
		this.container.addChild(mirrorRoot);

		const bodyPosition = body.GetPosition();
		const bodyAngle = body.GetAngle();
		const cos = Math.cos(bodyAngle);
		const sin = Math.sin(bodyAngle);
		const clone = clonePortalDisplayTree(sourceDisplay);
		const dx = sourceDisplay.x - bodyPosition.get_x() * this.ptm;
		const dy = sourceDisplay.y - bodyPosition.get_y() * this.ptm;
		clone.position.set(
			dx * cos + dy * sin,
			-dx * sin + dy * cos,
		);
		clone.rotation = sourceDisplay.rotation - bodyAngle;
		mirrorRoot.addChild(clone);

		// Do not trample a specialized mask such as a gore/decal mask. The
		// duplicate is still clipped; only that source-side visual keeps its
		// original masking behavior. A portal mask left behind by an interrupted
		// or leaked visual is not specialized: nothing draws it any more, so it
		// would clip the display to nothing. Reclaim it.
		const existingMask = sourceDisplay.mask;
		if(!existingMask || existingMask.isPortalPlaneMask) {
			if(existingMask) {
				sourceDisplay.mask = null;
				if(!existingMask.destroyed) existingMask.destroy();
			}
			const sourceMask = new PIXI.Graphics();
			sourceMask.label = PORTAL_PLANE_MASK_LABEL;
			sourceMask.isPortalPlaneMask = true;
			// Keep the mask inside the display it clips. Pixi resolves a mask's
			// local bounds relative to the masked container, so a mask elsewhere in
			// the tree makes getLocalBounds() (culling, width/height) intersect with
			// screen-space coordinates instead. Leaf displays cannot hold children
			// and keep the level-space placement.
			const local = sourceDisplay.allowChildren !== false;
			if(local) sourceDisplay.addChild(sourceMask);
			else this.container.addChild(sourceMask);
			this.sourceMasks.push({display: sourceDisplay, mask: sourceMask, local, body});
			sourceDisplay.mask = sourceMask;
		}

		const mirrorMask = new PIXI.Graphics();
		this.container.addChild(mirrorMask);
		mirrorRoot.mask = mirrorMask;
		this.entries.push({body, mirrorBody, mirrorRoot, mirrorMask});
	}

	update(deltaSeconds = 0) {
		if(!this.container || this.container.destroyed) return;
		const delta = Math.max(0, Math.min(0.05, Number(deltaSeconds) || 0));
		for(const entry of this.entries) {
			const position = entry.mirrorBody.GetPosition();
			entry.mirrorRoot.position.set(position.get_x() * this.ptm, position.get_y() * this.ptm);
			entry.mirrorRoot.rotation = entry.mirrorBody.GetAngle();
			drawPortalMask(
				entry.mirrorMask,
				this.candidate.target,
				this.ptm,
				mirrorPortalMaskQuads(
					this.candidate.target.apertureHalfLength * this.ptm,
					canRevealBeyondOpening(entry.body, this.candidate),
				),
			);
		}
		for(const sourceMask of this.sourceMasks) {
			// The level may have destroyed the display (and the mask with it)
			// before this visual is released.
			if(sourceMask.mask.destroyed) continue;
			// Every display in the connected island shares the same portal cut. This
			// includes sensor-only attachments such as eyes and welded helmets.
			drawPortalMask(
				sourceMask.mask,
				this.candidate.source,
				this.ptm,
				sourcePortalMaskQuads(
					this.candidate.source.apertureHalfLength * this.ptm,
					canRevealBeyondOpening(sourceMask.body, this.candidate),
				),
				sourceMask.local ?
					displayToParentMatrix(sourceMask.display, this.renderParent, tempMatrix) :
					null,
			);
		}
		for(const particles of this.intersectionParticles) particles.update(delta);
	}

	destroy() {
		for(const particles of this.intersectionParticles) particles.destroy();
		this.intersectionParticles.length = 0;
		for(const {display, mask} of this.sourceMasks) {
			if(!display.destroyed && display.mask === mask) display.mask = null;
			// Destroyed without options so the owned GraphicsContext is released;
			// Graphics.destroy(options) keeps it alive.
			if(!mask.destroyed) mask.destroy();
		}
		this.sourceMasks.length = 0;
		for(const {mirrorRoot, mirrorMask} of this.entries) {
			if(!mirrorRoot.destroyed) mirrorRoot.mask = null;
			if(!mirrorMask.destroyed) mirrorMask.destroy();
		}
		this.entries.length = 0;
		if(this.container && !this.container.destroyed) {
			this.container.destroy({
				children: true,
				context: true,
				texture: false,
				textureSource: false,
			});
		}
		this.container = null;
		this.renderParent = null;
	}
}
